import { useCallback, useEffect, useMemo } from 'react';

import type { DynamicAssessmentHost } from '@/modules/dynamicAssessments';

import { useOnboardingSteps } from './useOnboardingSteps';
import { useRulesEngine } from './useRulesEngine';

/**
 * The onboarding app's implementation of the dynamicAssessments module's
 * `DynamicAssessmentHost` contract — the single place the rules engine is wired
 * into `DynamicAssessmentScreen`.
 *
 * Named distinctly from the dynamicAssessments module's own `useDynamicAssessmentHost`,
 * which CONSUMES this contract off `DynamicAssessmentContext`. Two hooks, two sides of
 * one seam: this one builds, that one reads.
 *
 * The dependency direction is deliberate: `rulesEngine` imports the dynamicAssessments
 * module's TYPE, and the dynamicAssessments module imports nothing from `rulesEngine`. That's
 * what keeps `@/modules/dynamicAssessments` free of the rules engine (and transitively of
 * analytics → Datadog + PostHog, api → expo-secure-store, chat → stream-chat), so
 * the legacy screens that only want `AssessmentContent` — and any host app without an
 * onboarding rules engine — don't pay for it.
 *
 * See `docs/packaging-plan.md` §4.
 */
export function useOnboardingSurveyHost(): DynamicAssessmentHost {
  const {
    user,
    dynamicAssessmentEnabled,
    ensureDynamicAssessment,
    dynamicAssessmentErrored,
    markDynamicAssessmentErrored,
    deferCrisisProgram,
    enterCrisisProgram,
    setDynamicCompletedFlowKeys,
  } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();

  // `ResourceState<boolean>` → the host contract's tri-state. Anything that isn't
  // a resolved `true` (still loading, or an errored read that fails open to
  // control) leaves the screen on its spinner.
  const isEnabled = useMemo(
    () =>
      dynamicAssessmentEnabled.status === 'ready'
        ? dynamicAssessmentEnabled.value === true
        : undefined,
    [dynamicAssessmentEnabled]
  );

  // Latch which flows actually completed, then advance. `dynamicSurveyNextStep`
  // owns the survey-region order from here.
  const onCompleted = useCallback(
    (completedFlowKeys: readonly string[]) => {
      setDynamicCompletedFlowKeys(completedFlowKeys);
      goToNextStep({ from: '/step/dynamic-assessment' });
    },
    [setDynamicCompletedFlowKeys, goToNextStep]
  );

  // Belt-and-braces: `resolveOnboardingRoute` can't route to
  // /step/dynamic-assessment without `dynamicAssessment` already resolved (it
  // returns need('dynamicAssessment') first, which the driver answers with this
  // same call), so by the time the screen mounts the arm is normally read. Kept as
  // a safety net for any future path that reaches the screen without the driver
  // having resolved — `ensureDynamicAssessment` dedupes on a ref and is
  // `useCallback(…, [])`, so re-running is free and can't loop.
  //
  // Lives HERE rather than on the host contract: reading `onb_use_dynamic_assessment`
  // is an onboarding-app concern, and a host app implementing DynamicAssessmentHost
  // has no equivalent.
  useEffect(() => {
    ensureDynamicAssessment();
  }, [ensureDynamicAssessment]);

  return useMemo(
    () => ({
      userUuid: user?.uuid,
      isEnabled,
      hasErrored: dynamicAssessmentErrored,
      onCompleted,
      onCrisisDeferred: deferCrisisProgram,
      onCrisisImmediate: enterCrisisProgram,
      onFailed: markDynamicAssessmentErrored,
    }),
    [
      user?.uuid,
      isEnabled,
      dynamicAssessmentErrored,
      onCompleted,
      deferCrisisProgram,
      enterCrisisProgram,
      markDynamicAssessmentErrored,
    ]
  );
}
